import Analytics from '../analytics/Analytics';
import { analytics } from '../util/constants';

/**
 * @class AnalyticsService
 * @description Service to handle analytics-related functionalities.
 */
class AnalyticsService {
  /**
   * @type {Analytics}
   * @private
   * @description Instance of analytics to record events.
   */
  #analytics;
  /**
   * @type {RootStore}
   * @private
   */
  #rootStore;

  /**
   * @param {Analytics} analytics
   * @param {RootStore} rootStore
   */
  constructor(analytics, rootStore) {
    this.#analytics = analytics;
    this.#rootStore = rootStore;
  }

  addPaymentInfo = ({ products, productsTotal }) => {
    if (products.length) {
      try {
        this.#analytics.addPaymentInfo({
          productList: products,
          value: productsTotal,
        });
      } catch (error) {
        this.#logError('addPaymentInfo', error);
      }
    }
  };

  addShippingInfo = ({ products, productsTotal }) => {
    if (products.length) {
      try {
        this.#analytics.addShippingInfo({
          productList: products,
          value: productsTotal,
        });
      } catch (error) {
        this.#logError('addShippingInfo', error);
      }
    }
  };

  addToCart = ({ product, additionalProps }) => {
    try {
      this.#analytics.addToCart(
        [
          {
            product,
            ...additionalProps,
          },
        ],
        product.analytics.totalPrice
      );
    } catch (error) {
      this.#logError('addToCart', error);
    }
  };

  // KCO (Kustom Checkout) analytics methods
  kcoLoad = (eventData) => {
    try {
      // eventData: { customer: { type: "person|organization" }, shipping_address: { country: "swe", postal_code: "16972" } }
      this.#analytics.kcoLoad(eventData);
    } catch (error) {
      this.#logError('kcoLoad', error);
    }
  };

  kcoCustomer = (eventData) => {
    try {
      // eventData: { type: "person|organization" }
      this.#analytics.kcoCustomer(eventData);
    } catch (error) {
      this.#logError('kcoCustomer', error);
    }
  };

  kcoChange = (eventData) => {
    try {
      // eventData: { email: "...", postal_code: "...", country: "..." }
      this.#analytics.kcoChange(eventData);
    } catch (error) {
      this.#logError('kcoChange', error);
    }
  };

  kcoBillingAddressChange = (eventData) => {
    try {
      // eventData: { postal_code: "75511", country: "aut" }
      this.#analytics.kcoBillingAddressChange(eventData);
    } catch (error) {
      this.#logError('kcoBillingAddressChange', error);
    }
  };

  kcoShippingAddressChange = (eventData) => {
    try {
      // eventData: { postal_code: "75511", country: "aut" }
      this.#analytics.kcoShippingAddressChange(eventData);
    } catch (error) {
      this.#logError('kcoShippingAddressChange', error);
    }
  };

  kcoShippingOptionChange = (eventData) => {
    try {
      // eventData: { description: "...", id: "...", name: "...", price: 99999, promo: "...", tax_amount: 7621, tax_rate: 825 }
      this.#analytics.kcoShippingOptionChange(eventData);
    } catch (error) {
      this.#logError('kcoShippingOptionChange', error);
    }
  };

  kcoOrderTotalChange = (eventData) => {
    try {
      // eventData: { order_total: 6000 }
      this.#analytics.kcoOrderTotalChange(eventData);
    } catch (error) {
      this.#logError('kcoOrderTotalChange', error);
    }
  };

  kcoCheckboxChange = (eventData) => {
    try {
      // eventData: { key: "additional_merchant_terms_checkbox", checked: true }
      this.#analytics.kcoCheckboxChange(eventData);
    } catch (error) {
      this.#logError('kcoCheckboxChange', error);
    }
  };

  kcoNetworkError = (eventData) => {
    try {
      // eventData: {} (empty object)
      this.#analytics.kcoNetworkError(eventData);
    } catch (error) {
      this.#logError('kcoNetworkError', error);
    }
  };

  kcoRedirectInitiated = (eventData) => {
    try {
      // eventData: true
      this.#analytics.kcoRedirectInitiated(eventData);
    } catch (error) {
      this.#logError('kcoRedirectInitiated', error);
    }
  };

  kcoLoadConfirmation = (eventData) => {
    try {
      // eventData: {} (empty object)
      this.#analytics.kcoLoadConfirmation(eventData);
    } catch (error) {
      this.#logError('kcoLoadConfirmation', error);
    }
  };

  paymentStart = () => {
    try {
      this.#analytics.paymentStart();
    } catch (error) {
      this.#logError('paymentStart', error);
    }
  };

  removeFromCartCheckout = () => {
    const { checkoutStore } = this.#rootStore;
    const { order } = checkoutStore;
    const { removed_products } = order;

    if (removed_products.length === 0) {
      return;
    }

    const value = removed_products.reduce((sum, product) => {
      return sum + product.analytics.totalPrice;
    }, 0);

    try {
      this.#analytics.removeFromCart({
        productList: removed_products,
        value,
      });
    } catch (error) {
      this.#logError('removeFromCartCheckout', error);
      // Possibly some Sentry logger here
    }
  };

  removeFromCartStore = () => {
    const { cartStore } = this.#rootStore;
    const removedProducts = cartStore.getCartRemovedProducts();

    if (removedProducts.length === 0) {
      return;
    }

    const value = removedProducts.reduce((sum, product) => {
      return sum + product.analytics.totalPrice;
    }, 0);

    try {
      this.#analytics.removeFromCart({
        productList: removedProducts,
        value,
      });
    } catch (error) {
      this.#logError('removeFromCartStore', error);
      // Possibly some Sentry logger here
    }
  };

  viewCartCheckout = () => {
    const { productList, value } = this.#getBasicDataCheckout();
    const eventData = {
      itemListId: analytics.checkout.checkoutCartProductsListId,
      itemListName: analytics.checkout.checkoutCartProductsListName,
    };

    const data = {
      productList,
      value,
      eventData,
    };

    try {
      this.#analytics.viewCart(data);
    } catch (error) {
      this.#logError('viewCartCheckout', error);
    }
  };

  viewCartStore = () => {
    const { cartStore } = this.#rootStore;
    const { cart } = cartStore;
    const productList = cart?.products || [];
    const value = cart?.productsTotal?.value || null;

    const data = {
      productList,
      value,
    };

    try {
      this.#analytics.viewCart(data);
    } catch (error) {
      this.#logError('viewCartStore', error);
    }
  };

  viewItemCheckout = () => {
    const { checkoutStore } = this.#rootStore;
    const { checkoutCartModal } = checkoutStore;
    const { selectedProduct } = checkoutCartModal;

    const data = {
      productList: [
        {
          product: selectedProduct,
          itemListId: analytics.checkout.checkoutRecommendedProductsListId,
          itemListName: analytics.checkout.checkoutRecommendedProductsListName,
        },
      ],
      value: selectedProduct.analytics.totalPrice,
    };

    try {
      this.#analytics.viewItem(data);
    } catch (error) {
      this.#logError('viewItemCheckout', error);
    }
  };

  #logError = (origin, error) => {
    console.error(`${origin}: ${error}`);
  };

  #getBasicDataCheckout = () => {
    const { checkoutStore } = this.#rootStore;
    const { order } = checkoutStore;
    const productList = order?.productList || [];
    const value = order.productsTotal?.value || null;
    return { productList, value };
  };
}

export default AnalyticsService;
